/**
 * Bridge affiliate (per-partner) colors onto the Tailwind semantic color tokens declared in
 * assets/styles/main.css (`@theme static`, `tw:` prefix → emitted as `--tw-color-*` on :root).
 *
 * Returned entries ride the same inline `:style` binding as the `--app-*` props (see
 * libs/customizations.js and libs/embedded.js), so they override the :root value for the
 * layout subtree only. Custom properties inherit, so `tw:bg-primary`, `tw:text-text-primary`
 * and any component CSS using `var(--tw-color-primary)` pick the partner color up automatically.
 *
 * Colors only — spacing, radii, typography sizes and weights are intentionally NOT bridged.
 * Two tiers are mapped:
 *   - role tokens (primary, link, text-*, bg-*, border-*, card-label-*) → plain utilities;
 *   - component tokens (btn-*, field-*) → read by the Web Awesome bridge today and by the
 *     compat `.btn`/`.form-control` classes once those land. Bootstrap's own classes are
 *     still themed by the affiliate SCSS mixins, not by these.
 *   - nav tokens (nav-circle-*, nav-text-*, aside-*) and `accent` → for the guest-flow step nav
 *     and portal side nav once they read tokens; today those components still read `--app-*`.
 *
 * @param {object} styles       Merged partner + default `app*` style values.
 * @param {boolean} hasPartner  Whether the partner actually supplied customizations. When false
 *                              we return {} so non-affiliate users keep the Lendio palette
 *                              rather than inheriting the `--app-*` DEFAULT colors.
 */
export function getTailwindColorProps(styles, hasPartner) {
  if (!hasPartner || !styles) return {}

  const props = {
    '--tw-color-primary': styles.appPrimaryColor,
    // Mirrors `--app-focus-color` (primary + "80" alpha suffix) built by the style-prop builders.
    '--tw-color-focus': styles.appPrimaryColor ? styles.appPrimaryColor + 80 : undefined,
    '--tw-color-link': styles.appAnchorTagColor,
    '--tw-color-text-heading': styles.appBodyHeaderFontColor,
    '--tw-color-text-primary': styles.appBodyPrimaryFontColor,
    '--tw-color-bg-surface': styles.appBodyBackgroundColor,
    '--tw-color-border-card': styles.appCardBorderColor,
    '--tw-color-card-label-bg': styles.appCardLabelBackgroundColor,
    '--tw-color-card-label-text': styles.appCardLabelFontColor,
    // component tokens
    '--tw-color-btn-bg': styles.appButtonBackgroundColor,
    // Partners supply one button color; derive hover the way the affiliate SCSS does (0.9 opacity
    // ≈ 10% toward the page) until partners can send an explicit hover.
    '--tw-color-btn-bg-hover': styles.appButtonBackgroundColor
      ? `color-mix(in srgb, ${styles.appButtonBackgroundColor} 90%, white)`
      : undefined,
    '--tw-color-btn-text': styles.appButtonFontColor,
    // Strict legacy parity (_affiliate-customization-mixins.scss affiliateAppButtons):
    //   .btn-outline-primary text+border = primary; .btn-tertiary / .edit-button text = primary;
    //   .btn-outline-back bg = outline-button bg, hover border+text = button bg.
    '--tw-color-btn-secondary-border': styles.appPrimaryColor,
    '--tw-color-btn-secondary-text': styles.appPrimaryColor,
    '--tw-color-btn-tertiary-text': styles.appPrimaryColor,
    '--tw-color-btn-back-bg': styles.appOutlineButtonBackgroundColor,
    '--tw-color-btn-back-hover-border': styles.appButtonBackgroundColor,
    '--tw-color-btn-back-hover-text': styles.appButtonBackgroundColor,
    '--tw-color-field-bg': styles.appFormFieldBackgroundColor,
    '--tw-color-field-text': styles.appFormFieldValueColor,
    '--tw-color-field-label': styles.appFormFieldLabelColor,
    // navigation
    '--tw-color-nav-circle-active': styles.appNavCirclePrimary,
    '--tw-color-nav-circle-active-text': styles.appNavCircleTextPrimary,
    '--tw-color-nav-circle-completed': styles.appNavCircleCompleted,
    '--tw-color-nav-circle-completed-text': styles.appNavCircleTextCompleted,
    '--tw-color-nav-circle-disabled': styles.appNavCircleDisabled,
    '--tw-color-nav-circle-disabled-text': styles.appNavCircleTextDisabled,
    '--tw-color-nav-text-active': styles.appNavTextPrimary,
    '--tw-color-nav-text-completed': styles.appNavTextCompleted,
    '--tw-color-nav-text-disabled': styles.appNavTextDisabled,
    '--tw-color-aside-bg': styles.appAsideBackgroundColor,
    '--tw-color-aside-active-bg': styles.appAsideActiveBackgroundColor || styles.appAsideBackgroundColor,
    // accent
    '--tw-color-accent': styles.appSecondaryColor,
  }

  // Drop anything the partner (and defaults) left unset so the :root token still applies.
  return Object.fromEntries(Object.entries(props).filter(([, v]) => v != null && v !== ''))
}
